import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';

import {
    Button,
    CondensedTabs,
    Divider,
    Icon,
    IconTypes,
    Panel,
} from '@pexip/components';
import type {CallHistoryFavoriteItem, CallHistoryRecentItem} from '../../types';

import {CallHistoryItemView} from '../CallHistoryItem/CallHistoryItem.view';
import {FavoriteCallHistoryItemView} from '../FavoriteCallHistoryItem/FavoriteCallHistoryItem.view';
import {TestId} from '../../../test/testIds';

import styles from './CallHistoryPanel.module.scss';
import {PanelHeader} from '../PanelHeader/PanelHeader.view';
import {CallHistoryEmpty} from '../CallHistoryEmpty/CallHistoryEmpty.view';

export const CallHistoryPanelView: React.FC<{
    isFullWidth?: boolean;
    isMobile: boolean;
    isRoundedCorners: boolean;
    enableRecents?: boolean;
    enableFavorites?: boolean;
    fillHeight?: boolean;
    favorites: CallHistoryFavoriteItem[];
    recents: CallHistoryRecentItem[];
    selectedTab: 'favorites' | 'recents';
    className?: string;
    setSelectedTab: (tab: 'favorites' | 'recents') => void;
    deleteAllRecents: () => void;
    deleteAllFavorites: () => void;
    onClose: (e?: React.SyntheticEvent<HTMLElement>) => void;
    handleItemClick: (
        item: CallHistoryFavoriteItem | CallHistoryRecentItem,
    ) => void;
    handleRemoveFavorite: (item: CallHistoryFavoriteItem) => void;
    handleDeleteRecent: (item: CallHistoryRecentItem) => void;
    handleAddToFavorites: (item: CallHistoryRecentItem) => void;
    reorderItem?: (meetingId: string, newIndex: number) => void;
}> = ({
    isFullWidth = false,
    isMobile,
    isRoundedCorners,
    enableRecents = true,
    enableFavorites = true,
    fillHeight = true,
    favorites,
    recents,
    className,
    selectedTab,
    setSelectedTab,
    deleteAllRecents,
    deleteAllFavorites,
    onClose,
    handleItemClick,
    handleRemoveFavorite,
    handleDeleteRecent,
    handleAddToFavorites,
    reorderItem,
}) => {
    const {t} = useTranslation();
    const scrollElementRef = useRef<HTMLElement>(null);
    const [dragState, setDragState] = useState<{
        meetingId: string;
        originalIndex: number;
        dropIndex: number;
        itemHeight: number;
    } | null>(null);

    const reorderItemRef = useRef(reorderItem);
    const favoritesRef = useRef(favorites);
    useEffect(() => {
        reorderItemRef.current = reorderItem;
        favoritesRef.current = favorites;
    });

    const computeDragState = useCallback((draggedElement: HTMLDivElement) => {
        const meetingId = draggedElement.id;
        if (!meetingId) {
            return null;
        }
        const currentFavorites = favoritesRef.current;
        const originalIndex = currentFavorites.findIndex(
            fav => fav.meetingId === meetingId,
        );
        if (originalIndex === -1) {
            return null;
        }
        const container = draggedElement.parentElement;
        if (!container) {
            return null;
        }
        const itemHeight = draggedElement.offsetHeight;
        if (itemHeight === 0) {
            return null;
        }

        const containerTop = container.getBoundingClientRect().top;
        const draggedRect = draggedElement.getBoundingClientRect();
        const draggedMidY = draggedRect.top + draggedRect.height / 2;
        const relativeMidY = (draggedMidY - containerTop) / itemHeight;
        const dropIndex = Math.max(
            0,
            Math.min(Math.floor(relativeMidY), currentFavorites.length - 1),
        );

        return {meetingId, originalIndex, dropIndex, itemHeight};
    }, []);

    const handleDragMove = useCallback(
        (draggedElement: HTMLDivElement) => {
            if (!reorderItemRef.current) {
                return;
            }
            const next = computeDragState(draggedElement);
            if (!next) {
                return;
            }
            setDragState(prev => {
                if (
                    prev &&
                    prev.meetingId === next.meetingId &&
                    prev.originalIndex === next.originalIndex &&
                    prev.dropIndex === next.dropIndex &&
                    prev.itemHeight === next.itemHeight
                ) {
                    return prev;
                }
                return next;
            });
        },
        [computeDragState],
    );

    const handleDragEnd = useCallback(
        (draggedElement: HTMLDivElement) => {
            const callback = reorderItemRef.current;
            if (!callback) {
                setDragState(null);
                return;
            }
            const final = computeDragState(draggedElement);
            setDragState(null);
            if (!final) {
                return;
            }
            if (final.dropIndex === final.originalIndex) {
                return;
            }
            callback(final.meetingId, final.dropIndex);
        },
        [computeDragState],
    );

    let footerContent: React.ReactElement | undefined;

    const hasOnlyBrandingFavorites = favorites.every(fav => fav.isBranding);

    if (selectedTab === 'recents' && recents.length > 0) {
        footerContent = (
            <div className={styles.footer}>
                <Divider />
                <Button
                    noEnhancerPadding
                    enhancerStart={
                        <Icon source={IconTypes.IconTrash} size="compact" />
                    }
                    enhancerStartClassName="me-1"
                    variant="text"
                    onClick={deleteAllRecents}
                    data-testid={TestId.ButtonCallHistoryDeleteAllRecents}
                >
                    {t(
                        'media.call-history.delete-all-recents',
                        'Delete all recents',
                    )}
                </Button>
            </div>
        );
    } else if (
        selectedTab === 'favorites' &&
        favorites.length > 0 &&
        !hasOnlyBrandingFavorites
    ) {
        footerContent = (
            <div className={styles.footer}>
                <Divider />
                <Button
                    noEnhancerPadding
                    enhancerStart={
                        <Icon source={IconTypes.IconTrash} size="compact" />
                    }
                    enhancerStartClassName="me-1"
                    variant="text"
                    onClick={deleteAllFavorites}
                    data-testid={TestId.ButtonCallHistoryDeleteAllFavorites}
                >
                    {t(
                        'media.call-history.delete-all-favorites',
                        'Delete all favorites',
                    )}
                </Button>
            </div>
        );
    }

    const showEmptyMessage =
        (selectedTab === 'recents' && recents.length === 0) ||
        (selectedTab === 'favorites' && favorites.length === 0);

    return (
        <Panel
            scrollElementRef={scrollElementRef}
            className={cx(
                styles.panel,
                {[styles.fullWidth]: isFullWidth},
                className,
            )}
            headerClassName={styles.header}
            isWithBorder={false}
            isWithShadow
            fillHeight={fillHeight}
            isRounded={isRoundedCorners}
            padding={isMobile ? 'small' : 'compact'}
            footerPadding="none"
            footerBottomPadding={isMobile ? 'small' : 'compact'}
            headerPadding="none"
            headerContent={
                <PanelHeader
                    onCloseClick={onClose}
                    closeButtonTestId={TestId.ButtonCallHistoryPanelClose}
                    bottomPadding="none"
                    bottomContent={
                        <CondensedTabs
                            variant="underline"
                            allowActiveTabToBeClicked={false}
                            sizeModifier="medium"
                            selectedTabID={selectedTab}
                            onTabChange={id =>
                                setSelectedTab(id as 'favorites' | 'recents')
                            }
                            tabs={[
                                ...(enableRecents
                                    ? [
                                          {
                                              id: 'recents',
                                              name: t(
                                                  'media.call-history.recents',
                                                  'Recents',
                                              ),
                                              testId: TestId.ButtonCallHistoryRecentsTab,
                                          },
                                      ]
                                    : []),
                                ...(enableFavorites
                                    ? [
                                          {
                                              id: 'favorites',
                                              name: t(
                                                  'media.call-history.favorites',
                                                  'Favorites',
                                              ),
                                              testId: TestId.ButtonCallHistoryFavoritesTab,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    }
                ></PanelHeader>
            }
            footerContent={footerContent}
            hasFooterShadow={false}
        >
            {showEmptyMessage && <CallHistoryEmpty type={selectedTab} />}
            {selectedTab === 'recents' &&
                recents.map((item, index) => {
                    const isFavorite = favorites.some(
                        fav => fav.meetingId === item.meetingId,
                    );

                    return (
                        <CallHistoryItemView
                            className={
                                recents.length - 1 !== index
                                    ? styles.itemDivider
                                    : undefined
                            }
                            key={item.meetingId}
                            title={item.title}
                            callSucceeded={item.succeeded}
                            onClick={() => {
                                handleItemClick(item);
                                onClose();
                            }}
                            handleCallMeetingId={() => {
                                handleItemClick(item);
                                onClose();
                            }}
                            handleDeleteRecent={() => {
                                handleDeleteRecent(item);
                            }}
                            handleAddToFavorites={
                                !isFavorite
                                    ? () => {
                                          handleAddToFavorites(item);
                                      }
                                    : undefined
                            }
                            meetingId={item.meetingId}
                            direction={item.direction}
                            dateAndTime={item.dateAndTime}
                            imageUrl={item.avatarImageUrl}
                            isMobile={isMobile}
                        />
                    );
                })}
            {selectedTab === 'favorites' &&
                favorites.map((item, index) => {
                    const isItemDragging =
                        dragState?.meetingId === item.meetingId;
                    let dragShiftStyle: React.CSSProperties | undefined;
                    if (dragState && !isItemDragging) {
                        const {originalIndex, dropIndex, itemHeight} =
                            dragState;
                        const naturalIndexWithoutDragged =
                            index < originalIndex ? index : index - 1;
                        const finalIndex =
                            naturalIndexWithoutDragged +
                            (naturalIndexWithoutDragged >= dropIndex ? 1 : 0);
                        const shiftSlots = finalIndex - index;
                        dragShiftStyle = {
                            transform:
                                shiftSlots !== 0
                                    ? `translateY(${shiftSlots * itemHeight}px)`
                                    : undefined,
                            transition: 'transform 0.18s ease',
                        };
                    }
                    return (
                        <FavoriteCallHistoryItemView
                            key={item.meetingId}
                            item={item}
                            isMobile={isMobile}
                            showDivider={favorites.length - 1 !== index}
                            isDragDisabled={!reorderItem}
                            scrollElementRef={scrollElementRef}
                            style={dragShiftStyle}
                            onPointerMoveExtra={handleDragMove}
                            onPointerUpExtra={handleDragEnd}
                            onClick={() => {
                                handleItemClick(item);
                                onClose();
                            }}
                            handleRemoveFavorite={() => {
                                if (!item.isBranding) {
                                    handleRemoveFavorite(item);
                                }
                            }}
                            handleReorderByKeyboard={direction => {
                                if (!reorderItem) {
                                    return;
                                }

                                if (direction === 'up') {
                                    if (index === 0) {
                                        return;
                                    }
                                    reorderItem(item.meetingId, index - 1);
                                    return;
                                }

                                if (index === favorites.length - 1) {
                                    return;
                                }
                                reorderItem(item.meetingId, index + 1);
                            }}
                        />
                    );
                })}
        </Panel>
    );
};

export type CallHistoryPanelViewProps = React.ComponentProps<
    typeof CallHistoryPanelView
>;
